import { INVESTOR_ROLES, MANAGE_USERS_TABS, RESTRICTED_ROLES, USER_TYPES } from '../store/investor/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DealFilters } from '../store/investor/reducer';
import { toast } from 'react-toastify';
import { TALK_TO_US_OWNERS } from './constants/commonConstants';
import { USER_TYPE } from './constants/user';
import { CountryCodeData } from '../modules/Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';

export const LAKH = 100000;
export const CRORE = 10000000;

export const APP_ENV = {
    PRODUCTION: 'production',
    DEV: 'development',
    STAGING: 'staging',
};

export const history: any = {
    navigate: null,
    location: null,
};

export interface ExtractPhoneNumberResult {
    phoneNumber: string;
    countryCodeIndex: number;
}

export const isMobile = window.innerWidth < 800;
export const emailRegex = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/);
export const websiteUrlRegex = new RegExp(
    /^(?:(ftp|http|https):\/\/)?(?:([a-zA-Z0-9-]+)\.)?(?:[\w-]+\.)+[a-z]{2,}(?:\.[a-z]{2,})?(?:\/)?$/,
);
export const linkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.[a-z.]+\/(in|company|school|groups)\/[A-Za-z0-9_-]+(\/)?(\?.*)?$/;

export let is_prod = process.env.REACT_APP_ENVIRONMENT === 'production' && location.hostname !== 'localhost';

export function isEmpty(value: any) {
    return (
        value === undefined ||
        value === null ||
        // || value === NaN
        (typeof value === 'object' && Object.keys(value).length === 0) ||
        (typeof value === 'string' && value.trim().length === 0) ||
        (typeof value === 'number' && isNaN(Number(value)))
    );
}

export function getSubdomain() {
    const url = window.location.toString();
    const regex = /^https:\/\/([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(\/[^\s?]*)?(?:\?[^#\s]*)?$/;
    const match = url.match(regex);
    if (match && match.length > 1) {
        return match?.[1]?.split('.')?.[0];
    }

    if (process.env.REACT_APP_ENVIRONMENT === APP_ENV.STAGING) return 'aica-staging';
    if (process.env.REACT_APP_ENVIRONMENT !== APP_ENV.PRODUCTION) return 'aica-dev';
    return '';
}

export function getDomain() {
    if (process.env.REACT_APP_ENVIRONMENT !== APP_ENV.PRODUCTION) return 'founderlink.ai';
    const hostname = window.location.hostname;
    let parts = hostname.split('.');
    if (parts.length <= 2) return hostname;

    parts = parts.slice(-3);
    if (['co', 'com'].indexOf(parts[1]) > -1) return parts.join('.');

    return parts.slice(-2).join('.');
}

export function convertArrayToMap(arr, key): {}[] {
    return arr.reduce(function (map, obj) {
        map[obj[key]] = obj;
        return map;
    }, {});
}

export function getInitialsFromName(string, takeLastWord = true) {
    if (!string) return '';

    const names = string.trim().split(/\s+/);
    let initials = names[0]?.substring(0, 1).toUpperCase() || '';

    if (names.length > 1) {
        const secondInitial = takeLastWord ? names[names.length - 1]?.substring(0, 1) : names[1]?.substring(0, 1);
        initials += secondInitial?.toUpperCase() || '';
    }

    return initials;
}

export async function copyTextToClipboard(text) {
    if ('clipboard' in navigator) {
        return await navigator.clipboard.writeText(text);
    } else {
        return document.execCommand('copy', true, text);
    }
}

export function getFeatureStatus(availableFeatures: string[] = [], featureName) {
    let value: any = '';
    availableFeatures.map((obj) => {
        const featureKey = Object.keys(obj ?? {})[0];
        if (featureKey === featureName) value = Object?.values(obj)?.[0] || '';
    });
    return value;
}

export const validateGSTUserName = (gstUserName: string) => {
    let isValid = true;
    let message = '';
    if (!gstUserName.match(/^([a-zA-Z0-9_\-.]){8,15}$/)) {
        isValid = false;
        message = 'Please enter a valid username';
    }
    return {
        isValid,
        message,
    };
};

export const clearLocalStorageOnLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
};

export const handleBackgroundScroll = (isScrollable: boolean) => {
    if (isScrollable) {
        document.body.style.overflow = 'auto';
    } else {
        document.body.style.overflow = 'hidden';
    }
};

export const convertToTitleCase = (input = '') => {
    // Check if input contains underscore
    if (input?.includes?.('_')) {
        // Split input by underscore and capitalize each word
        return input
            ?.split?.('_')
            ?.map?.((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())
            ?.join(' ');
    } else {
        // Split?. camelCase input? into individual words and capitalize them
        return input
            ?.split?.(/(?=[A-Z])/)
            ?.map?.((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())
            ?.join(' ');
    }
};

export const convertToSnakeCase = (input = '') => {
    // Check if input contains spaces
    if (input?.includes?.(' ')) {
        // Split input by spaces and convert to lowercase
        return input
            ?.split?.(' ')
            ?.map?.((word) => word?.toLowerCase())
            ?.join('_');
    }
};

export const convertToCapitalizeUnderscore = (input: string = ''): string =>
    input
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('_');

export const getFromLocalStorage = (keyName: string = '') => {
    if (keyName) {
        return localStorage.getItem(keyName) ? JSON.parse(localStorage.getItem(keyName) || '') : null;
    }
    return null;
};

export const getPlatformUserId = (): string => {
    if (sessionStorage.getItem('platform_user_id')) {
        return sessionStorage.getItem('platform_user_id') ?? '';
    }
    return localStorage.getItem('platform_user_id') ?? '';
};

export const isRestrictedUser = (role: null | string, restrictedRoles: string[]) => {
    if (isEmpty(role)) {
        return false;
    }
    if (restrictedRoles.includes(role ?? '')) {
        return true;
    }
    return false;
};
export const numberOnly = (value) => {
    if (!value) return '';
    return value.replace(/[^0-9]/g, '');
};

export function convertToFirstLetterCapital(string) {
    return string.toLowerCase().replace(/\b\w/g, function (letter) {
        return letter.toUpperCase();
    });
}

export function capitalizeWords(str = '') {
    return str?.toLowerCase()?.replace(/\b\w/g, function (char) {
        return char.toUpperCase();
    });
}

export function decodeHtmlEntities(html) {
    return html?.replace(/&#(\d+);/g, function (match, dec) {
        return String.fromCharCode(dec);
    });
}

export function removeKeyAndConvertIdToNumber(array, keyToRemove) {
    array.forEach((obj) => {
        delete obj[keyToRemove];
    });
    return array;
}

export function shortNumberFormat(number) {
    if (Math.abs(number) >= 1.0e6) {
        return (Math.abs(number) / 1.0e6).toFixed(1) + 'M';
    } else if (Math.abs(number) >= 1.0e3) {
        return (Math.abs(number) / 1.0e3).toFixed(0) + 'k';
    } else {
        return number.toString();
    }
}

export function formatWebsiteUrl(websiteUrl = '') {
    let modifiedString = websiteUrl;
    if (websiteUrl?.charAt(websiteUrl?.length - 1) === '/') modifiedString = websiteUrl?.substring(0, websiteUrl?.length - 1);
    return modifiedString?.replace('https://', '')?.replace('http://', '')?.replace('www.', '') || '';
}

export function changeFavicon(newFaviconUrl: string) {
    let link: any = document.querySelector("link[rel~='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = newFaviconUrl;
}

export function setRolesForUser(role = '') {
    if (RESTRICTED_ROLES.includes(role)) {
        switch (role) {
            case INVESTOR_ROLES.RESTRICTED_UNDERWRITER_ACCESS:
                return 'Underwriter';
            case INVESTOR_ROLES.RESTRICTED_INVESTOR_ACCESS:
                return 'RRF Investor';
        }
    }
    return 'AICA_Investor';
}

export function unflatten(data: { [key: string]: any }, options: { includeNull?: boolean } = { includeNull: false }) {
    const result = {};
    Object.keys(data).forEach((key) => {
        const keys = key.split('.');
        keys.reduce((r: any, e: string, j) => {
            if (!r[e]) {
                if (Number.isNaN(Number(keys[j + 1]))) {
                    if (keys.length - 1 === j) {
                        // Include the value if it's not null/undefined or if includeNull is true
                        if (options.includeNull || (data[key] !== null && data[key] !== undefined)) {
                            r[e] = data[key];
                        }
                    } else {
                        r[e] = {};
                    }
                } else {
                    r[e] = [];
                }
            }
            return r[e];
        }, result);
    });
    return result;
}

export const flatten = (obj: { [key: string]: any }, prefix = '') => {
    let flattened = {};

    for (let key in obj) {
        if (obj?.hasOwnProperty(key)) {
            let newKey = prefix ? `${prefix}.${key}` : key;

            if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
                Object.assign(flattened, flatten(obj[key], newKey));
            } else {
                flattened[newKey] = obj[key];
            }
        }
    }

    return flattened;
};

export const convertEpochToDate = (epoch: number | string) => {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    const utcTime = dayjs.utc(epoch);
    const istTime = utcTime.tz('Asia/Kolkata');

    return istTime.format('hh:mm A, DD MMM YYYY');
};

export function getObjAndFileNameS3(url) {
    if (Number.isInteger(Number(url))) {
        const obj = { url };
        return { obj, fileName: 'file', extention: null };
    }
    const commonStringInUrl = 'amazonaws.com/';
    const indexOfCommonString = url?.indexOf(commonStringInUrl);
    const bucket = getBucketNameFromS3Url(url);
    const urlToSend = url.slice(indexOfCommonString + commonStringInUrl.length);
    const obj = { url: urlToSend, bucket: bucket };
    const fileName = urlToSend.substr(urlToSend.indexOf('/') + 1);

    const extentionArr = obj?.url?.split('.');
    const extention = extentionArr[extentionArr?.length - 1];
    return { obj, fileName, extention };
}

export function getBucketNameFromS3Url(url) {
    const urlParts = url.split('/');
    if (urlParts.length >= 3 && urlParts[2].endsWith('.amazonaws.com')) {
        const urlParts1 = urlParts[2].split('.');
        if (urlParts1.length > 0) {
            return urlParts1[0];
        } else {
            return null;
        }
    } else {
        return null;
    }
}

export function validateCIN(cin: string): boolean {
    const cinPattern = new RegExp(/^([LUu]{1})([0-9]{5})([A-Za-z]{2})([0-9]{4})([A-Za-z]{3})([0-9]{6})$/);
    return cinPattern.test(cin);
}

/**
 * Pass details in query params to be stored in session storage
 *
 * @param summonDetails
 */
export function openCompanySummonTab(userData: {
    accountType: string;
    parentOrgId: string;
    orgId: string;
    primaryEmail: string;
    userId: string;
    userName: string;
    parentEmailId: string;
    authToken: string;
    userRole: string;
}): void {
    const { orgId, userId, authToken, accountType, parentOrgId, primaryEmail, userName, parentEmailId } = userData;
    // Open a new tab with the summon details in query params
    const summonUrl = `${
        window.location.origin
    }/company/upload?orgId=${orgId}&userId=${userId}&token=${authToken}&accountType=${accountType}&parentOrgId=${parentOrgId}&primaryEmail=${encodeURIComponent(
        primaryEmail,
    )}&userName=${encodeURIComponent(userName)}&parentEmailId=${encodeURIComponent(parentEmailId)}&userRole=${userData.userRole}`;
    window.open(summonUrl, '_blank');
}

/**
 * Pass details in query params to be stored in session storage
 *
 * @param summonDetails
 */
export function openUserSummonTab(userData: {
    accountType: string;
    parentOrgId: string;
    orgId: string;
    primaryEmail: string;
    userId: string;
    userName: string;
    parentEmailId: string;
    authToken: string;
    userRole: string;
    inviteCode: string;
    platformUserId: string;
    referrer?: string;
}): void {
    const { orgId, userId, authToken, accountType, parentOrgId, primaryEmail, userName, parentEmailId, inviteCode, platformUserId } =
        userData;
    // Open a new tab with the summon details in query params
    let summonUrl = `${
        window.location.origin
    }/investor/app/scan?orgId=${orgId}&userId=${userId}&token=${authToken}&accountType=${accountType}&parentOrgId=${parentOrgId}&primaryEmail=${encodeURIComponent(
        primaryEmail,
    )}&userName=${encodeURIComponent(userName)}&parentEmailId=${encodeURIComponent(parentEmailId)}&userRole=${
        userData.userRole
    }&inviteCode=${encodeURIComponent(inviteCode)}&platformUserId=${platformUserId}&summonInternal=true`;
    if (userData.referrer) {
        summonUrl += `&referrer=${userData.referrer}`;
    }
    window.open(summonUrl, '_blank');
}

export function toTitleCase(str: string): string {
    if (!str) return '';
    return str
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/**
 * Get domain from url
 */
export function getDomainFromUrl(url: string): string {
    if (!url) return '';
    try {
        const urlObj = new URL(url);
        return urlObj.hostname.replace('www.', '');
    } catch (error) {
        return url;
    }
}

export function getHashFromId(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

/**
 * Returns a human-readable string representing how long ago a date was
 * @param date - ISO 8601 date string
 * @returns "Today", "Yesterday", or "X days ago"
 * @throws Error if date is invalid or in the future
 */
export function returnDaysAgo(date: string): string {
    if (!date) {
        throw new Error('Date is required');
    }

    const parsedDate = dayjs(date);
    if (!parsedDate.isValid()) {
        throw new Error('Invalid date format');
    }

    const now = dayjs();
    if (parsedDate.isAfter(now)) {
        throw new Error('Future dates are not supported');
    }

    const diff = now.diff(parsedDate, 'day');
    return diff === 1 ? 'Yesterday' : diff === 0 ? 'Today' : `${diff} days ago`;
}

/**
 * Safely evaluates a formula based on a given condition and expression, substituting variables with their values.
 * @param formula - An object containing:
 *   - `fallback`: (Optional) The fallback number to return if the condition is not met.
 *   - `condition`: (Optional) A string representing the condition to evaluate.
 *   - `expression`: A string representing the expression to evaluate.
 * @param variables - A record of variable names and their numeric values.
 * @returns The result of the evaluated expression if the condition is met, otherwise the fallback value.
 */
export function evaluateFormula(formula, variables) {
    if (!formula.expression) {
        throw new Error('Expression is required for evaluation.');
    }

    const replaceVariables = (str: string) =>
        str.replace(/(\w+)/g, (match) => {
            return variables.hasOwnProperty(match) ? variables[match] : 0;
        });

    const conditionWithValues = formula.condition ? replaceVariables(formula.condition) : 'true';
    const expressionWithValues = replaceVariables(formula.expression);

    try {
        const conditionFn = new Function(`return ${conditionWithValues};`);
        const expressionFn = new Function(`return ${expressionWithValues};`);

        return conditionFn() ? expressionFn() : formula.fallback ?? 0;
    } catch (error) {
        console.error('Invalid formula evaluation', error);
    }
}

export const checkIfAnyFilterSelected = (filters: DealFilters) => {
    const filtersToCheck = ['statuses', 'dealOwners', 'dealReceivers', 'uwAnalysts', 'dcmOwners', 'leadOwners', 'products'] as const;
    type FilterKey = (typeof filtersToCheck)[number];

    return filtersToCheck.some((filter: FilterKey) => Array.isArray(filters[filter]) && (filters[filter] as string[])?.length > 0);
};

export const navigateUserAfterUnauth = (location, navigate) => {
    if (location.pathname.includes('investor/')) navigate('/investor/login');
    else if (location.pathname.includes('lender/')) navigate('/investor/login');
    else navigate('/login');
};

export const truncateTextForComments = (text: string, smaller = true) => {
    const lines = text.split('\n');
    if (lines.length === 1) {
        // If there are no newline characters, show up to 60 characters with "..."
        return lines[0].length > 20 ? lines[0].slice(0, smaller ? 20 : 40) + '...' : lines[0];
    }
    // If there are newline characters, truncate first line after 50 characters
    const truncatedLines = lines.slice(0, 1).map((line) => (line.length > 20 ? line.slice(0, smaller ? 20 : 40) + '...' : line));

    return truncatedLines.join('\n') + (lines.length > 2 ? '...' : '');
};

export const getUserTypeList = (userType: string | null, parentCategory?: string | null) => {
    const effectiveCategory = userType || parentCategory;

    switch (effectiveCategory) {
        case MANAGE_USERS_TABS.PARTNER:
            return [USER_TYPES.PARTNER_TYPE];
        case MANAGE_USERS_TABS.INVESTOR:
            return [USER_TYPES.LENDER, USER_TYPES.LENDER_TEAM];
        case MANAGE_USERS_TABS.TEAM:
            return [USER_TYPES.INVESTOR_TYPE, USER_TYPES.ADMIN_TEAM];
        case MANAGE_USERS_TABS.COMPANY:
            return [USER_TYPES.COMPANY_TYPE];
        default:
            return [];
    }
};

export const getOwnerDetails = (userType: string) => {
    switch (userType) {
        case USER_TYPE.PARTNER:
            return TALK_TO_US_OWNERS.PARTNER;
        case USER_TYPE.LENDER:
        case USER_TYPE.LENDER_TEAM:
            return TALK_TO_US_OWNERS.LENDER;
        case USER_TYPE.COMPANY:
            return TALK_TO_US_OWNERS.BORROWER;
        default:
            return TALK_TO_US_OWNERS.BORROWER;
    }
};

export const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
};

export const truncateByCharacters = (input: string, limit: number) => {
    if (!input) return '';
    return input.length <= limit ? input : input.slice(0, limit) + '...';
};

export const deepCompare = (obj1: any, obj2: any): boolean => {
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return obj1 === obj2;
    }

    if (obj1 instanceof Map && obj2 instanceof Map) {
        if (obj1.size !== obj2.size) {
            return false;
        }
        for (const [key, value] of obj1) {
            if (!obj2.has(key) || !deepCompare(value, obj2.get(key))) {
                return false;
            }
        }
        return true;
    }

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) {
            return false;
        }
        return obj1.every((item, index) => deepCompare(item, obj2[index]));
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
};

export const isLenderType = (userType: string = '') => {
    if (isEmpty(userType)) {
        userType = sessionStorage.getItem('user_data')
            ? JSON.parse(sessionStorage.getItem('user_data') || '{}').accountType
            : JSON.parse(localStorage.getItem('user_data') || '{}').accountType;
    }
    return [USER_TYPES.LENDER, USER_TYPES.LENDER_TEAM].includes(userType);
};

/**
 * Replaces Handlebars placeholders in a string with actual values from templateValues.
 * Supports both regular {{key}} and nested {{{key}}} placeholder patterns.
 *
 * Processing Order:
 * 1. First processes nested triple braces {{{key}}} which may resolve to {{value}} or direct values
 * 2. Then processes regular double braces {{key}} to final values
 * 3. Repeats until no more replacements can be made or max iterations reached
 *
 * Features:
 * - Handles multiple placeholders of the same type in a single template
 * - Processes placeholders from left to right for predictable results
 * - Supports recursive/nested placeholder resolution (e.g., {{{key}}} -> {{value}} -> finalValue)
 * - Prevents infinite loops with iteration limits and change detection
 * - Gracefully handles missing keys by leaving placeholders unchanged
 *
 * @param template - String containing Handlebars placeholders like {{variableName}} or {{{variableName}}}
 * @param templateValues - Object containing key-value pairs for placeholder replacement
 * @returns String with all resolvable placeholders replaced with their corresponding values
 */
export const replaceTemplatePlaceholders = (template: string, templateValues: any): string => {
    if (!template || typeof template !== 'string') {
        return '';
    }

    let result = template;
    let previousResult = '';
    let iterations = 0;
    const maxIterations = 10; // Prevent infinite loops

    // Keep processing until no more changes occur or max iterations reached
    while (result !== previousResult && iterations < maxIterations) {
        previousResult = result;
        iterations++;

        // First, handle nested braces like {{{key}}} -> {{value}} or actualValue
        while (result.includes('{{{') && result.includes('}}}')) {
            let startIndex = result.indexOf('{{{');
            if (startIndex === -1) break;

            let endIndex = result.indexOf('}}}', startIndex);
            if (endIndex === -1) break;

            // Extract the key between {{{ and }}}
            const innerKey = result.substring(startIndex + 3, endIndex).trim();
            const replacement = templateValues[innerKey] || `{{{${innerKey}}}}`;

            // Replace this specific occurrence
            result = result.substring(0, startIndex) + replacement + result.substring(endIndex + 3);

            // If no replacement was made (key not found), break to avoid infinite loop
            if (replacement === `{{{${innerKey}}}`) {
                break;
            }
        }
        // Then, handle regular double braces {{key}} -> value
        Object.keys(templateValues).forEach((key) => {
            const placeholder = `{{${key}}}`;
            const value = templateValues[key] || '';

            // Replace all occurrences of this placeholder
            while (result.includes(placeholder)) {
                result = result.replace(placeholder, value);
            }
        });
    }

    // Log if we hit max iterations (potential infinite loop)
    if (iterations >= maxIterations) {
        console.warn('Template placeholder replacement hit maximum iterations. Possible circular reference.');
    }

    return result;
};

type FilterOperator = 'IN' | 'EQUALS' | 'BETWEEN';

/**
 * Transforms dynamic filters based on the provided prefixes and operator.
 *
 * @param filters - The filters object to transform.
 * @param dynamicFieldPrefixes - The prefixes of dynamic fields to check.
 * @param defaultOperator - The default operator to use if a dynamic field is found.
 */
export const transformDynamicFilters = (
    filters: Record<string, any>,
    dynamicFieldPrefixes: string[] = ['customFields.', 'owners.'],
    defaultOperator: FilterOperator = 'IN',
): Record<string, { operator: FilterOperator; value: any[]; valueType?: string }> => {
    const dynamicFields: Record<string, { operator: FilterOperator; value: any[]; valueType?: string }> = {};

    Object.entries(filters).forEach(([key, value]) => {
        const isDynamicField = dynamicFieldPrefixes.some((prefix) => key.startsWith(prefix));

        if (isDynamicField && Array.isArray(value) && value.length > 0) {
            // Check if this is a DateRangeFilter by looking for gte/lte structure (fallback)
            dynamicFields[key] = {
                operator: defaultOperator,
                value,
            };
        } else if (isDynamicField && typeof value === 'object' && !Array.isArray(value)) {
            // If the value is an object, i.e we send the operator and any other keys from the filter renderer
            dynamicFields[key] = value;
        }
    });

    return dynamicFields;
};

export const isAppliedFiltersEmpty = (filters: Record<string, any[]> | undefined | null): boolean => {
    if (!filters || Object.keys(filters).length === 0) {
        return true;
    }

    // Check if all values are empty arrays
    return Object.values(filters).every((value) => Array.isArray(value) && value.length === 0);
};

/**
 *Persist the state of customFields while sending in the api payload.
 *This is done to persist the undefined or null values of customFields in order to clear the fields in BE and UI.
 *avoid from sending customFields as {} in the api payload.
 * @param customFields - The custom fields object to clean
 * @returns The cleaned custom fields object
 */
export function cleanCustomFields(customFields: any) {
    if (!customFields || typeof customFields !== 'object') return customFields;

    const cleaned: Record<string, any> = {};
    Object.entries(customFields).forEach(([key, value]) => {
        cleaned[key] = value === undefined ? null : value;
    });
    return cleaned;
}

/**
 * Creates single select dropdown options from data
 * @param data - Array of organization objects with organizationId and organizationDisplayName
 * @returns Object with organizationId as keys and dropdown option objects as values
 */
export const createDropdownOptions = (
    data: Array<{ organizationId: string; organizationDisplayName: string }> | null | undefined,
): Record<string, any> => {
    if (!data || !Array.isArray(data)) return {};
    const dropdownOptions = {};
    data.forEach((org) => {
        dropdownOptions[org.organizationId] = {
            label: org.organizationDisplayName,
            value: org.organizationId,
            backgroundColor: '#EBEBEB',
            labelColor: '#000000',
            customCaret: 'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/grey-dropdown-caret.svg',
        };
    });
    return dropdownOptions;
};

export const copyInviteToClipboard = (inviteCode: string) => {
    const url = new URL(`https://${window.location.hostname}`);
    url.searchParams.set('inviteCode', inviteCode);
    copyTextToClipboard(url.href)
        .then(() => {
            toast.success('Invite Link Copied');
        })
        .catch(() => toast.success('Something went wrong'));
};
export const isSafeUrl = (url: string): boolean => {
    try {
        const u = new URL(url, window.location.origin);
        const allowed = ['https:', 'mailto:'];
        return allowed.includes(u.protocol);
    } catch {
        return false;
    }
};

export const isPnoUser = (userType: string = '') => {
    return userType === USER_TYPES.ADMIN_TEAM || userType === USER_TYPES.INVESTOR_TYPE;
};

/**
 * Extracts phone number and country code index from mobileNumber string
 * @param mobileNumber - The full mobile number string (may include country code)
 * @param userCountryCode - Optional country code from user data
 * @param countryCodes - Array of available country codes
 * @returns Object containing extracted phoneNumber and countryCodeIndex
 */
export const extractPhoneNumber = (
    mobileNumber: string,
    userCountryCode: string | undefined,
    countryCodes: CountryCodeData[],
): ExtractPhoneNumberResult => {
    if (!mobileNumber?.trim()) {
        return { phoneNumber: '', countryCodeIndex: -1 };
    }

    if (!countryCodes?.length) {
        return { phoneNumber: mobileNumber.replace(/[+\s-]/g, ''), countryCodeIndex: -1 };
    }

    let phoneNumber = '';
    let countryCodeIndex = -1;

    // First, try to match country code from userData.countryCode
    if (userCountryCode) {
        const matchedCountryCode = countryCodes.find((cc) => cc.countryCode === userCountryCode);
        if (matchedCountryCode) {
            countryCodeIndex = countryCodes.findIndex((cc) => cc.countryCode === userCountryCode);
            const callingCodePrefix = `+${matchedCountryCode.callingCode}`;
            const callingCodeWithSpace = `${callingCodePrefix} `;

            // Extract phone number by removing the country code prefix
            if (mobileNumber.startsWith(callingCodeWithSpace)) {
                phoneNumber = mobileNumber.substring(callingCodeWithSpace.length);
                return { phoneNumber, countryCodeIndex };
            }
            if (mobileNumber.startsWith(callingCodePrefix)) {
                phoneNumber = mobileNumber.substring(callingCodePrefix.length);
                return { phoneNumber, countryCodeIndex };
            }
            // If no prefix found, clean the number
            phoneNumber = mobileNumber.replace(/[+\s-]/g, '');
            return { phoneNumber, countryCodeIndex };
        }
    }

    // Fallback: parse from mobileNumber string
    if (!mobileNumber.startsWith('+')) {
        phoneNumber = mobileNumber.replace(/[+\s-]/g, '');
        return { phoneNumber, countryCodeIndex: -1 };
    }

    // Try to match country code from the mobile number prefix
    for (const cc of countryCodes) {
        const callingCode = `+${cc.callingCode}`;
        const callingCodeWithSpace = `${callingCode} `;

        if (mobileNumber.startsWith(callingCodeWithSpace)) {
            phoneNumber = mobileNumber.substring(callingCodeWithSpace.length);
            countryCodeIndex = countryCodes.findIndex((c) => c.countryCode === cc.countryCode);
            return { phoneNumber, countryCodeIndex };
        }
        if (mobileNumber.startsWith(callingCode)) {
            phoneNumber = mobileNumber.substring(callingCode.length);
            countryCodeIndex = countryCodes.findIndex((c) => c.countryCode === cc.countryCode);
            return { phoneNumber, countryCodeIndex };
        }
    }

    // Default fallback: clean the number
    phoneNumber = mobileNumber.replace(/[+\s-]/g, '');
    return { phoneNumber, countryCodeIndex: -1 };
};

export const filterColumnToMap = (map, column) => {
    const existing = map.get(column.field);

    if (!existing || (column?.show !== false && existing?.show === false)) {
        map.set(column.field, column);
    }
};
